import { supabase } from './supabase';

declare global {
  interface Window {
    MusicKit: any;
  }
}

let musicKit: any = null;

export const initializeAppleMusic = async (developerToken: string) => {
  try {
    await loadMusicKitScript();
    
    musicKit = await window.MusicKit.configure({
      developerToken,
      app: {
        name: 'Appsoundlink',
        build: '1.0.0',
      },
    });

    return musicKit;
  } catch (error) {
    console.error('Error initializing Apple Music:', error);
    throw error;
  }
};

const loadMusicKitScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.MusicKit) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load MusicKit JS'));
    document.body.appendChild(script);
  });
};

export const connectAppleMusic = async () => {
  try {
    // Get current user's ID for state parameter
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get the current session for the authorization header
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No active session');

    // Initialize auth flow
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/apple-music-auth`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to initialize Apple Music authentication');
    }

    const { token } = await response.json();
    if (!token) {
      throw new Error('No developer token received');
    }

    // Initialize MusicKit with the developer token
    const music = await initializeAppleMusic(token);

    try {
      // Request user authorization
      const musicUserToken = await music.authorize();
      
      if (!musicUserToken) {
        throw new Error('Failed to get user token');
      }

      // Update user profile
      const { error: updateError } = await supabase
        .from('users')
        .update({
          apple_music_connected: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Return success without reloading
      return true;
    } catch (authError) {
      console.error('Apple Music authorization error:', authError);
      throw new Error('Failed to authorize with Apple Music. Please try again.');
    }
  } catch (error) {
    console.error('Error connecting to Apple Music:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred. Please try again.');
  }
};

export const getUserLibrary = async () => {
  try {
    if (!musicKit) {
      throw new Error('MusicKit not initialized');
    }

    const library = await musicKit.api.library.songs();
    return library;
  } catch (error) {
    console.error('Error fetching user library:', error);
    throw error;
  }
};

export const getRecentlyPlayed = async () => {
  try {
    if (!musicKit) {
      throw new Error('MusicKit not initialized');
    }

    const recent = await musicKit.api.recentPlayed();
    return recent;
  } catch (error) {
    console.error('Error fetching recently played:', error);
    throw error;
  }
};